import React, { useEffect, useRef } from "react";
import {
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";

export type MarketContextDefinitionRow = {
  metric: string;
  definition: string;
};

export function MarketContextDefinitionsModal(props: {
  isOpen: boolean;
  onClose: () => void;
  rows: MarketContextDefinitionRow[];
  selectedMetric: string | null;
  definitionsRef?: string | null;
  textColor: string;
  subtleText: string;
  borderColor: string;
  panelBg: string;
}): React.ReactElement {
  const metricDefinitionRowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  const metricDefinitionScrollRef = useRef<HTMLDivElement | null>(null);
  const pendingMetricScrollRef = useRef<string | null>(null);

  useEffect(() => {
    if (props.isOpen && props.selectedMetric) {
      pendingMetricScrollRef.current = props.selectedMetric;
    }
  }, [props.isOpen, props.selectedMetric]);

  useEffect(() => {
    if (!props.isOpen) return;
    const metric = pendingMetricScrollRef.current ?? props.selectedMetric;
    if (!metric) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 12;

    const tryScroll = () => {
      if (cancelled) return;
      const container = metricDefinitionScrollRef.current;
      const row = metricDefinitionRowRefs.current[metric];
      if (container && row) {
        const targetTop = row.offsetTop - container.clientHeight / 2 + row.clientHeight / 2;
        container.scrollTo({ top: Math.max(0, targetTop), behavior: "smooth" });
        pendingMetricScrollRef.current = null;
        return;
      }
      attempts += 1;
      if (attempts >= maxAttempts) return;
      window.setTimeout(tryScroll, 50);
    };

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(tryScroll);
    });

    return () => {
      cancelled = true;
    };
  }, [props.isOpen, props.rows, props.selectedMetric]);

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} size="4xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Definitions</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={5}>
          <Text fontSize="sm" color={props.subtleText} mb={3} fontFamily="'IBM Plex Sans', sans-serif">
            Use this glossary to understand the references and terminology used throughout this report.
          </Text>
          {props.definitionsRef ? (
            <Text fontSize="xs" color={props.subtleText} mb={3} fontFamily="mono">
              ref: {props.definitionsRef}
            </Text>
          ) : null}
          {props.rows.length === 0 ? (
            <Text fontSize="sm" color={props.subtleText}>
              Definitions are not available for this slice.
            </Text>
          ) : (
            <Box
              ref={metricDefinitionScrollRef}
              borderWidth="1px"
              borderColor={props.borderColor}
              borderRadius="6px"
              overflow="auto"
              maxH="60vh"
            >
              <Table variant="simple" size="sm" minW="760px">
                <Thead position="sticky" top={0} zIndex={1} bg={props.panelBg}>
                  <Tr>
                    <Th color={props.subtleText} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                      Metric
                    </Th>
                    <Th color={props.subtleText} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                      Definition
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {props.rows.map((row) => (
                    <Tr
                      key={row.metric}
                      ref={(el: HTMLTableRowElement | null) => {
                        metricDefinitionRowRefs.current[row.metric] = el;
                      }}
                      bg={props.selectedMetric === row.metric ? "rgba(56, 187, 209, 0.10)" : undefined}
                      _hover={{ bg: "rgba(56, 187, 209, 0.06)" }}
                    >
                      <Td
                        fontSize="sm"
                        color={props.textColor}
                        fontFamily="'IBM Plex Sans', sans-serif"
                        fontWeight="semibold"
                        whiteSpace="nowrap"
                        verticalAlign="top"
                      >
                        {row.metric}
                      </Td>
                      <Td
                        fontSize="sm"
                        color={props.textColor}
                        fontFamily="'IBM Plex Sans', sans-serif"
                        lineHeight="1.5"
                        whiteSpace="normal"
                      >
                        {row.definition}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
